import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  INTERNAL_NETWORK_DISCOVERY_COMPLETED_SSE_EVENT,
  INTERNAL_NETWORK_DISCOVERY_FAILED_SSE_EVENT,
  INTERNAL_NETWORK_DISCOVERY_STAGE_CHANGED_SSE_EVENT,
  INTERNAL_NETWORK_DISCOVERY_STARTED_SSE_EVENT,
  INTERNAL_NETWORK_ENROLLMENT_HEARTBEAT_SSE_EVENT,
  INTERNAL_NETWORK_SCENARIO_STATUS_CHANGED_SSE_EVENT,
  type DiscoveryStageChangedPayload,
} from '@terra/shared-contracts-internal-network';
import { useSSE } from '../app/providers/SseProvider';
import { INTERNAL_NETWORK_GRAPH_KEY } from './api/useInternalNetworkGraph';
import { INTERNAL_NETWORK_DISCOVERY_RUNS_KEY } from './api/useInternalNetworkOverview';
import { INTERNAL_NETWORK_SCENARIOS_KEY } from './api/useInternalNetworkScenarios';
import { INTERNAL_NETWORK_STATS_KEY } from './api/useInternalNetworkStats';
import {
  INTERNAL_NETWORK_ENROLLMENTS_KEY,
  INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY,
} from './api/useSurfaceSettings';

const DISCOVERY_FINISHED_QUERY_KEYS = [
  INTERNAL_NETWORK_EXTERNAL_NETWORK_DETAIL_KEY,
  INTERNAL_NETWORK_STATS_KEY,
  INTERNAL_NETWORK_GRAPH_KEY,
] as const;

export function useInternalNetworkSSE({ networkId }: { networkId: string }) {
  const { subscribe } = useSSE();
  const queryClient = useQueryClient();

  useEffect(() => {
    const isForNetwork = (event: { networkId: string }) =>
      event.networkId === networkId;

    const handleDiscoveryProgress = (data: DiscoveryStageChangedPayload) => {
      void queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_DISCOVERY_RUNS_KEY, data.networkId],
      });
    };

    const handleDiscoveryFinished = (data: DiscoveryStageChangedPayload) => {
      handleDiscoveryProgress(data);
      DISCOVERY_FINISHED_QUERY_KEYS.forEach((key) => {
        void queryClient.invalidateQueries({
          queryKey: [key, data.networkId],
        });
      });
    };

    const unsubscribers = [
      subscribe(
        INTERNAL_NETWORK_DISCOVERY_STARTED_SSE_EVENT,
        handleDiscoveryProgress,
        { filter: isForNetwork },
      ),
      subscribe(
        INTERNAL_NETWORK_DISCOVERY_STAGE_CHANGED_SSE_EVENT,
        handleDiscoveryProgress,
        { filter: isForNetwork },
      ),
      subscribe(
        INTERNAL_NETWORK_DISCOVERY_COMPLETED_SSE_EVENT,
        handleDiscoveryFinished,
        { filter: isForNetwork },
      ),
      subscribe(
        INTERNAL_NETWORK_DISCOVERY_FAILED_SSE_EVENT,
        handleDiscoveryFinished,
        { filter: isForNetwork },
      ),
      subscribe(
        INTERNAL_NETWORK_SCENARIO_STATUS_CHANGED_SSE_EVENT,
        (data) => {
          void queryClient.invalidateQueries({
            queryKey: [INTERNAL_NETWORK_SCENARIOS_KEY, data.signalId],
          });
        },
        { filter: isForNetwork },
      ),
      subscribe(
        INTERNAL_NETWORK_ENROLLMENT_HEARTBEAT_SSE_EVENT,
        (data) => {
          void queryClient.invalidateQueries({
            queryKey: [INTERNAL_NETWORK_ENROLLMENTS_KEY, data.networkId],
          });
        },
        { filter: isForNetwork },
      ),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [networkId, queryClient, subscribe]);
}
